import React, { useEffect, useRef, useState } from "react";
import { WHATSAPP_START_URL } from "../siteConfig";
import previewAvatar from "../assets/optimized/profile-avatar-96.webp";
import { Icon } from "./Icon";

const MOBILE_MEDIA_QUERY = "(max-width: 767px)";
const TITLE_NOWRAP_PHRASE = "WhatsApp voice notes";

export const Header = ({
  data,
  isTranscriptionCountLoading = false,
  transcriptionCount = null,
}) => {
  const heroTitle = data?.title || "Turn voice notes and images into text";
  const primaryCtaLabel = data?.primaryCta || "Convert now";
  const primaryCtaSubline = data?.primaryCtaSubline || "(Free)";
  const mobileInlineCtaRef = useRef(null);
  const countAnimationRef = useRef(null);
  const countAnimationTimeoutRef = useRef(null);
  const previousCountRef = useRef(transcriptionCount);
  const [showStickyMobileCta, setShowStickyMobileCta] = useState(false);
  const [displayedTranscriptionCount, setDisplayedTranscriptionCount] =
    useState(transcriptionCount);
  const [isCountIncreasing, setIsCountIncreasing] = useState(false);
  const formattedTranscriptionCount =
    typeof displayedTranscriptionCount === "number"
      ? new Intl.NumberFormat("en-US").format(displayedTranscriptionCount)
      : null;
  const waveformBars = [
    16, 24, 36, 22, 30, 40, 24, 18, 34, 42, 30, 24, 20, 28, 36, 24, 18, 16,
  ];

  const renderHeroTitle = () => {
    if (!heroTitle.includes(TITLE_NOWRAP_PHRASE)) {
      return heroTitle;
    }

    const [before, after] = heroTitle.split(TITLE_NOWRAP_PHRASE);

    return (
      <>
        {before}
        <span className="hero-title-nowrap">{TITLE_NOWRAP_PHRASE}</span>
        {after}
      </>
    );
  };

  const renderPrimaryCtaContent = () => (
    <>
      <span className="cta-whatsapp-icon" aria-hidden="true">
        <Icon name="whatsapp" />
      </span>
      <span className="hero-primary-cta-label">{primaryCtaLabel}</span>
      <span className="hero-primary-cta-subline">{primaryCtaSubline}</span>
    </>
  );

  const renderHeroLiveStat = () => {
    if (isTranscriptionCountLoading && !formattedTranscriptionCount) {
      return (
        <div className="hero-live-stat hero-live-stat-inline hero-live-stat-loading-wrap">
          <span className="hero-live-stat-loading">Loading live count...</span>
        </div>
      );
    }

    if (!formattedTranscriptionCount) {
      return null;
    }

    return (
      <div className="hero-live-stat hero-live-stat-inline">
        <span
          className={`hero-live-stat-value${
            isCountIncreasing ? " is-increasing" : ""
          }`}
        >
          {formattedTranscriptionCount}
        </span>
        <span className="hero-live-stat-label">messages converted to date</span>
      </div>
    );
  };

  useEffect(() => {
    if (typeof transcriptionCount !== "number") {
      return undefined;
    }

    const previousCount = previousCountRef.current;

    if (typeof previousCount !== "number") {
      previousCountRef.current = transcriptionCount;
      setDisplayedTranscriptionCount(transcriptionCount);
      return undefined;
    }

    if (transcriptionCount <= previousCount) {
      previousCountRef.current = transcriptionCount;
      setDisplayedTranscriptionCount(transcriptionCount);
      setIsCountIncreasing(false);
      return undefined;
    }

    if (typeof window === "undefined") {
      previousCountRef.current = transcriptionCount;
      setDisplayedTranscriptionCount(transcriptionCount);
      return undefined;
    }

    const animationDurationMs = 900;
    const animationStart = window.performance.now();
    const startCount = previousCount;
    const endCount = transcriptionCount;

    setIsCountIncreasing(true);

    if (countAnimationTimeoutRef.current) {
      window.clearTimeout(countAnimationTimeoutRef.current);
    }

    const step = (now) => {
      const progress = Math.min(
        1,
        (now - animationStart) / animationDurationMs
      );
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const nextCount = Math.floor(
        startCount + (endCount - startCount) * easedProgress
      );

      setDisplayedTranscriptionCount((currentCount) => {
        if (typeof currentCount !== "number") {
          return nextCount;
        }

        return nextCount > currentCount ? nextCount : currentCount;
      });

      if (progress < 1) {
        countAnimationRef.current = window.requestAnimationFrame(step);
        return;
      }

      setDisplayedTranscriptionCount(endCount);
      countAnimationTimeoutRef.current = window.setTimeout(() => {
        setIsCountIncreasing(false);
      }, 260);
    };

    countAnimationRef.current = window.requestAnimationFrame(step);
    previousCountRef.current = transcriptionCount;

    return () => {
      if (countAnimationRef.current) {
        window.cancelAnimationFrame(countAnimationRef.current);
      }

      if (countAnimationTimeoutRef.current) {
        window.clearTimeout(countAnimationTimeoutRef.current);
      }
    };
  }, [transcriptionCount]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    let observer;

    const updateObserver = () => {
      setShowStickyMobileCta(false);

      if (observer) {
        observer.disconnect();
        observer = null;
      }

      if (!mediaQuery.matches || !mobileInlineCtaRef.current) {
        return;
      }

      observer = new IntersectionObserver(
        ([entry]) => {
          setShowStickyMobileCta(!entry.isIntersecting);
        },
        {
          root: null,
          rootMargin: "-96px 0px 0px 0px",
          threshold: 0,
        }
      );

      observer.observe(mobileInlineCtaRef.current);
    };

    const handleMediaChange = () => {
      updateObserver();
    };

    updateObserver();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleMediaChange);
    } else {
      mediaQuery.addListener(handleMediaChange);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }

      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", handleMediaChange);
      } else {
        mediaQuery.removeListener(handleMediaChange);
      }
    };
  }, []);

  useEffect(
    () => () => {
      if (typeof window === "undefined") {
        return;
      }

      if (countAnimationRef.current) {
        window.cancelAnimationFrame(countAnimationRef.current);
      }

      if (countAnimationTimeoutRef.current) {
        window.clearTimeout(countAnimationTimeoutRef.current);
      }
    },
    []
  );

  return (
    <header id="header" className="landing-hero">
      <div
        className={`hero-mobile-sticky-cta${
          showStickyMobileCta ? " is-visible" : ""
        }`}
      >
        <a
          href={WHATSAPP_START_URL}
          className="btn btn-custom btn-lg hero-primary-cta hero-mobile-sticky-button"
          data-analytics-label="sticky_mobile_transcribe_now"
          target="_blank"
          rel="noopener noreferrer"
        >
          {renderPrimaryCtaContent()}
        </a>
      </div>

      <div className="landing-hero-backdrop">
        <div className="container">
          <div className="row landing-hero-grid hero-top-grid">
            <div className="col-md-6 landing-hero-copy hero-copy-wrap">
              <h1 className="hero-main-title">
                {renderHeroTitle()}
                {data?.highlight ? (
                  <>
                    {" "}
                    <span className="hero-highlight-pill">{data.highlight}</span>
                  </>
                ) : null}
              </h1>

              <div ref={mobileInlineCtaRef} className="hero-mobile-inline-cta">
                <a
                  href={WHATSAPP_START_URL}
                  className="btn btn-custom btn-lg hero-primary-cta hero-mobile-inline-button"
                  data-analytics-label="mobile_inline_transcribe_now"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {renderPrimaryCtaContent()}
                </a>
              </div>

              <p className="hero-description">
                {data?.paragraph ||
                  "Read voice notes when you can't listen, and copy text from screenshots and photos without retyping. All inside WhatsApp."}
              </p>

              <div className="hero-proof-cta-row">
                <div className="hero-actions hero-desktop-actions">
                  <a
                    href={WHATSAPP_START_URL}
                    className="btn btn-custom btn-lg hero-primary-cta hero-desktop-primary-button"
                    data-analytics-label="desktop_transcribe_now"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {renderPrimaryCtaContent()}
                  </a>
                </div>

                {renderHeroLiveStat()}
              </div>

              <p className="hero-cta-note hero-cta-note-inline">
                {data?.ctaNote ||
                  "Forward a voice note or send an image with text to get started."}
              </p>
            </div>

            <div className="col-md-6 landing-hero-panel-wrap hero-preview-column">
              <div className="hero-preview-stage">
                <div className="preview-audio-bubble message-bubble-sent">
                  <div className="preview-bubble-meta">Forwarded</div>
                  <div className="preview-audio-row">
                    <img
                      src={previewAvatar}
                      alt="Profile"
                      className="preview-avatar"
                      decoding="async"
                      height="96"
                      width="96"
                    />
                    <div className="preview-play-button">
                      <span className="preview-play-icon"></span>
                    </div>
                    <div className="preview-waveform" aria-hidden="true">
                      {waveformBars.map((height, index) => (
                        <span
                          key={`${height}-${index}`}
                          className="preview-wave-bar"
                          style={{ height: `${height}px` }}
                        ></span>
                      ))}
                    </div>
                  </div>
                  <div className="preview-audio-footer">
                    <span>0:43</span>
                    <span className="preview-audio-status">
                      <span>08:43</span>
                      <span className="preview-checks">✓✓</span>
                    </span>
                  </div>
                </div>

                <div className="preview-text-bubble message-bubble-received">
                  <div className="preview-transcription-copy">
                    <p>
                      Hey, sorry, I cannot type properly right now, so sending
                      this as a voice note.
                    </p>
                    <p>
                      The meeting later has been moved to 2:30 instead of 1:00,
                      and they want us to present the new app.
                    </p>
                    <p>
                      Please can you fix the typo on page 4 and email it to me?
                    </p>
                    <p>See you later!</p>
                  </div>
                  <div className="preview-text-meta">08:43</div>
                </div>

                <div className="preview-summary-bubble message-bubble-received">
                  <p className="preview-box-title preview-box-title-green">
                    Summary
                  </p>
                  <ul className="preview-summary-list">
                    <li>The meeting moved from 1:00 to 2:30.</li>
                    <li>Present the new app in the meeting.</li>
                  </ul>
                  <p className="preview-box-title preview-box-title-green">
                    Call to action
                  </p>
                  <ul className="preview-summary-list preview-action-list">
                    <li>
                      Fix the typo on page 4 and email the updated version.
                    </li>
                  </ul>
                  <div className="preview-text-meta">08:43</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
